import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatApiError } from '@/api/errors';
import {
  TRANSACTION_CATEGORIES,
  classifyGlAccounts,
  createGlAccount,
  discoverGlAccounts,
  importGlAccounts,
  listAllGlAccounts,
} from '@/api/gl-accounts';
import type { GlAccountCreateIn, GlAccountOut, TransactionCategory } from '@/api/gl-accounts';
import { Dialog, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/format';
import { useAuth } from '../auth/AuthContext';
import type { GlAccountSortKey } from '../hooks/useGlAccountWorkbench';
import { isDisagreement, useGlAccountWorkbench } from '../hooks/useGlAccountWorkbench';

const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  meals_and_entertainment: 'Meals & entertainment',
  interest_expense: 'Interest expense',
  accrual_no_invoice: 'Accrual (no invoice)',
  exempt_capital_transaction: 'Exempt capital',
  uncategorized: 'Uncategorized',
};

const ITC_TREATMENTS = ['restricted_50_meals', 'no_itc_exempt_supply'] as const;

const SOURCE_LABELS: Record<string, string> = {
  observed: 'Observed',
  coa_import: 'COA import',
  gifi_seed: 'GIFI seed',
  keyword_seed: 'Keyword seed',
  practitioner: 'Practitioner',
};

const selectClass =
  'appearance-none rounded-md border border-input bg-background px-3 py-1.5 pr-8 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal';
const inputClass =
  'rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal';

export function GlAccountWorkbench({
  slug,
  initialAccount,
  onInitialAccountConsumed,
}: {
  slug: string;
  initialAccount?: string;
  /** Called once the deep-linked drawer opens (or the param can't
   *  resolve) so the owner can drop `account` from the URL — otherwise
   *  re-entering the tab reopens a drawer the user already closed. */
  onInitialAccountConsumed?: () => void;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [drawerAccount, setDrawerAccount] = useState<GlAccountOut | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['gl-accounts', slug, userKey],
    enabled: isSignedIn,
    queryFn: async () => listAllGlAccounts(slug, (await getIdToken()) ?? ''),
  });
  const items = accountsQuery.data?.items ?? [];
  const stats = accountsQuery.data?.stats;
  const workbench = useGlAccountWorkbench(items);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['gl-accounts', slug] });

  const discover = useMutation({
    mutationFn: async () => discoverGlAccounts(slug, (await getIdToken()) ?? ''),
    onMutate: () => setActionError(null),
    onSuccess: invalidate,
    onError: (err) => setActionError(formatApiError(err)),
  });
  const importChart = useMutation({
    mutationFn: async (file: File) => importGlAccounts(slug, file, (await getIdToken()) ?? ''),
    onMutate: () => setActionError(null),
    onSuccess: invalidate,
    onError: (err) => setActionError(formatApiError(err)),
  });
  const create = useMutation({
    mutationFn: async (body: GlAccountCreateIn) =>
      createGlAccount(slug, body, (await getIdToken()) ?? ''),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      invalidate();
      setAddOpen(false);
    },
    onError: (err) => setActionError(formatApiError(err)),
  });

  const classify = useMutation({
    mutationFn: async (body: {
      gl_account_names: string[];
      category?: TransactionCategory | null;
      gifi_code?: string | null;
      itc_treatment?: string | null;
      is_tax_account?: boolean | null;
      notes?: string | null;
    }) => classifyGlAccounts(slug, body, (await getIdToken()) ?? ''),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      invalidate();
      setSelected(new Set());
      setDrawerAccount(null);
    },
    onError: (err) => setActionError(formatApiError(err)),
  });

  // Prune selections that fell out of the visible set on filter change.
  const visibleNames = new Set(workbench.visible.map((i) => i.gl_account_name));
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((name) => visibleNames.has(name)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workbench.visible.length, workbench.search, workbench.accountType, workbench.classified]);

  // Deep link from a finding card: open the drawer for the named
  // account once the list arrives (exact name, or leaf-segment match
  // since finding evidence carries the full NetSuite hierarchy path).
  const openedInitialRef = useRef(false);
  useEffect(() => {
    if (!initialAccount || openedInitialRef.current || items.length === 0) return;
    openedInitialRef.current = true;
    const leaf = (name: string) => name.split(':').pop()?.trim() ?? name;
    const exact = items.find((i) => i.gl_account_name === initialAccount);
    // Leaf fallback only when unambiguous — two hierarchies can share
    // a leaf name, and silently opening the wrong account is worse
    // than opening none.
    const leafMatches = exact
      ? []
      : items.filter((i) => leaf(i.gl_account_name) === leaf(initialAccount));
    const match = exact ?? (leafMatches.length === 1 ? leafMatches[0] : undefined);
    if (match) setDrawerAccount(match);
    onInitialAccountConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialAccount, items]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const onImportFile = (file: File | null) => {
    if (file) importChart.mutate(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (accountsQuery.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading GL accounts…</p>;
  }
  if (accountsQuery.isError) {
    return (
      <p className="text-sm text-destructive">{formatApiError(accountsQuery.error)}</p>
    );
  }

  const pctClassified =
    stats && stats.total > 0 ? Math.round((stats.classified / stats.total) * 100) : 0;

  const ctas = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={(e) => onImportFile(e.target.files?.[0] ?? null)}
      />
      <Button
        variant="outline"
        size="sm"
        disabled={importChart.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="mr-1 size-3.5" />
        {importChart.isPending ? 'Importing…' : 'Import chart of accounts'}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={discover.isPending}
        onClick={() => discover.mutate()}
      >
        {discover.isPending ? 'Discovering…' : 'Discover from transactions'}
      </Button>
      <Button size="sm" onClick={() => setAddOpen(true)}>
        Add account
      </Button>
    </div>
  );

  if (items.length === 0) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>No GL accounts yet</CardTitle>
          <CardDescription>
            Import the customer&apos;s chart of accounts (CSV or xlsx with name, code, type, and
            GIFI columns — NetSuite&apos;s native export works as-is), or discover accounts from transactions already in the system. New audit
            runs register their accounts automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {ctas}
          {actionError && <p className="text-sm text-destructive">{actionError}</p>}
        </CardContent>
        {addOpen && (
          <AddGlAccountDialog
            open
            pending={create.isPending}
            errorText={actionError}
            onClose={() => setAddOpen(false)}
            onSubmit={(body) => create.mutate(body)}
          />
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="GL accounts" value={String(stats?.total ?? items.length)} />
        <StatCard label="Classified" value={`${pctClassified}%`} />
        <StatCard
          label="$ through unclassified accounts"
          value={formatCurrency(Number(stats?.unclassified_debit_total ?? 0))}
          emphasize={Number(stats?.unclassified_debit_total ?? 0) > 0}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            placeholder="Search name or code…"
            value={workbench.search}
            onChange={(e) => workbench.setSearch(e.target.value)}
            className={inputClass}
          />
          <FilterSelect
            value={workbench.accountType}
            onChange={workbench.setAccountType}
            options={[
              { value: 'all', label: 'All types' },
              ...workbench.accountTypes.map((t) => ({ value: t, label: t })),
            ]}
          />
          <FilterSelect
            value={workbench.classified}
            onChange={(v) => workbench.setClassified(v as typeof workbench.classified)}
            options={[
              { value: 'all', label: 'All accounts' },
              { value: 'unclassified', label: 'Unclassified' },
              { value: 'classified', label: 'Classified' },
              { value: 'disagreement', label: 'Classifier disagrees' },
            ]}
          />
        </div>
        {ctas}
      </div>

      {actionError && (
        <p className="text-sm text-destructive" role="alert">
          {actionError}
        </p>
      )}

      {selected.size > 0 && (
        <BulkBar
          count={selected.size}
          pending={classify.isPending}
          onApply={(category, treatment) =>
            classify.mutate({
              gl_account_names: [...selected],
              category: category || undefined,
              itc_treatment: treatment || undefined,
            })
          }
          onClear={() => setSelected(new Set())}
        />
      )}

      <GlAccountTable
        items={workbench.visible}
        selected={selected}
        setSelected={setSelected}
        sortKey={workbench.sortKey}
        onSort={workbench.setSortKey}
        onOpen={setDrawerAccount}
      />

      {addOpen && (
        <AddGlAccountDialog
          open
          pending={create.isPending}
          errorText={actionError}
          onClose={() => setAddOpen(false)}
          onSubmit={(body) => create.mutate(body)}
        />
      )}

      {drawerAccount && (
        <GlAccountDrawer
          account={drawerAccount}
          pending={classify.isPending}
          errorText={actionError}
          onClose={() => setDrawerAccount(null)}
          onSave={(fields) =>
            classify.mutate({ gl_account_names: [drawerAccount.gl_account_name], ...fields })
          }
        />
      )}
    </div>
  );
}

/** Manual entry — for accounts the registry hasn't seen yet (no runs
 *  ingested, or an account outside the uploaded chart). */
function AddGlAccountDialog({
  open,
  pending,
  errorText,
  onClose,
  onSubmit,
}: {
  open: boolean;
  pending: boolean;
  errorText: string | null;
  onClose: () => void;
  onSubmit: (body: GlAccountCreateIn) => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [accountType, setAccountType] = useState('');
  const [gifiCode, setGifiCode] = useState('');
  const [category, setCategory] = useState<TransactionCategory | ''>('');
  const [treatment, setTreatment] = useState('');
  const [isTaxAccount, setIsTaxAccount] = useState(false);
  const [notes, setNotes] = useState('');

  const submit = () => {
    if (!name.trim()) return;
    onSubmit({
      gl_account_name: name.trim(),
      gl_account_code: code.trim() || null,
      account_type: accountType.trim() || null,
      gifi_code: gifiCode.trim() || null,
      category: category || null,
      itc_treatment: treatment || null,
      is_tax_account: isTaxAccount,
      notes: notes.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} width="max-w-lg">
      <DialogHeader>
        <DialogTitle>Add GL account</DialogTitle>
      </DialogHeader>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1 block font-medium">Account name *</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. 6301 - Meals & Entertainment"
            className={`${inputClass} w-full`}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Code</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={32}
            placeholder="6301"
            className={`${inputClass} w-full`}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Type</span>
          <input
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            maxLength={32}
            placeholder="Expense"
            className={`${inputClass} w-full`}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">GIFI code</span>
          <input
            value={gifiCode}
            onChange={(e) => setGifiCode(e.target.value)}
            maxLength={4}
            placeholder="8523"
            className={`${inputClass} w-full`}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Category</span>
          <FilterSelect
            value={category}
            onChange={(v) => setCategory(v as TransactionCategory | '')}
            options={[
              { value: '', label: 'Unclassified' },
              ...TRANSACTION_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] })),
            ]}
          />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">ITC treatment</span>
          <FilterSelect
            value={treatment}
            onChange={setTreatment}
            options={[
              { value: '', label: 'None' },
              ...ITC_TREATMENTS.map((t) => ({ value: t, label: t })),
            ]}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isTaxAccount}
            onChange={(e) => setIsTaxAccount(e.target.checked)}
          />
          <span>Tax account</span>
        </label>
        <label className="block text-sm sm:col-span-2">
          <span className="mb-1 block font-medium">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={512}
            rows={2}
            className={`${inputClass} w-full`}
          />
        </label>
      </div>
      {errorText && (
        <p className="mt-3 text-sm text-destructive" role="alert">
          {errorText}
        </p>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={pending || !name.trim()} onClick={submit}>
          {pending ? 'Adding…' : 'Add account'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function StatCard({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <p className="text-xs uppercase tracking-wide text-brand-slate">{label}</p>
        <p className={`mt-1 text-2xl font-semibold ${emphasize ? 'text-destructive' : ''}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
    </div>
  );
}

function BulkBar({
  count,
  pending,
  onApply,
  onClear,
}: {
  count: number;
  pending: boolean;
  onApply: (category: TransactionCategory | '', treatment: string) => void;
  onClear: () => void;
}) {
  const [category, setCategory] = useState<TransactionCategory | ''>('');
  const [treatment, setTreatment] = useState('');
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-brand-royal/40 bg-brand-royal/5 px-3 py-2 text-sm">
      <span className="font-medium">{count} selected</span>
      <FilterSelect
        value={category}
        onChange={(v) => setCategory(v as TransactionCategory | '')}
        options={[
          { value: '', label: 'Category…' },
          ...TRANSACTION_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] })),
        ]}
      />
      <FilterSelect
        value={treatment}
        onChange={setTreatment}
        options={[
          { value: '', label: 'ITC treatment…' },
          ...ITC_TREATMENTS.map((t) => ({ value: t, label: t })),
        ]}
      />
      <Button
        size="sm"
        disabled={pending || (!category && !treatment)}
        onClick={() => onApply(category, treatment)}
      >
        {pending ? 'Applying…' : 'Apply to selected'}
      </Button>
      <Button variant="ghost" size="sm" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}

function GlAccountTable({
  items,
  selected,
  setSelected,
  sortKey,
  onSort,
  onOpen,
}: {
  items: GlAccountOut[];
  selected: Set<string>;
  setSelected: (next: Set<string>) => void;
  sortKey: GlAccountSortKey;
  onSort: (key: GlAccountSortKey) => void;
  onOpen: (account: GlAccountOut) => void;
}) {
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.gl_account_name));
  const someSelected = items.some((i) => selected.has(i.gl_account_name));
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(items.map((i) => i.gl_account_name)));
  };
  const toggleOne = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    setSelected(next);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>
            <input
              ref={headerCheckboxRef}
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              aria-label="Select all visible accounts"
            />
          </TableHead>
          <TableHead>Code</TableHead>
          <SortHeader label="Account" sortKey="name" active={sortKey} onSort={onSort} />
          <TableHead>Type</TableHead>
          <TableHead>GIFI</TableHead>
          <TableHead>Category</TableHead>
          <TableHead>ITC treatment</TableHead>
          <SortHeader
            label="Txns"
            sortKey="transactions"
            active={sortKey}
            onSort={onSort}
            align="right"
          />
          <SortHeader
            label="$ total"
            sortKey="volume"
            active={sortKey}
            onSort={onSort}
            align="right"
          />
          <SortHeader label="Last seen" sortKey="lastSeen" active={sortKey} onSort={onSort} />
          <TableHead>Source</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={item.gl_account_name}
            className="cursor-pointer"
            onClick={() => onOpen(item)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen(item);
              }
            }}
            tabIndex={0}
          >
            <TableCell onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={selected.has(item.gl_account_name)}
                onChange={() => toggleOne(item.gl_account_name)}
                aria-label={`Select ${item.gl_account_name}`}
              />
            </TableCell>
            <TableCell className="font-mono text-xs">{item.gl_account_code ?? '—'}</TableCell>
            <TableCell className="font-medium">
              {item.gl_account_name}
              {item.is_tax_account && (
                <Badge variant="outline" className="ml-2">
                  tax account
                </Badge>
              )}
            </TableCell>
            <TableCell>{item.account_type ?? '—'}</TableCell>
            <TableCell className="font-mono text-xs">{item.gifi_code ?? '—'}</TableCell>
            <TableCell>
              <CategoryBadge item={item} />
            </TableCell>
            <TableCell className="text-xs">{item.itc_treatment ?? '—'}</TableCell>
            <TableCell className="text-right">{item.transaction_count}</TableCell>
            <TableCell className="text-right">
              {formatCurrency(Number(item.total_debit))}
            </TableCell>
            <TableCell className="text-xs">{formatDate(item.last_seen_at)}</TableCell>
            <TableCell className="text-xs">
              {SOURCE_LABELS[item.classification_source] ?? item.classification_source}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SortHeader({
  label,
  sortKey,
  active,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: GlAccountSortKey;
  active: GlAccountSortKey;
  onSort: (key: GlAccountSortKey) => void;
  align?: 'left' | 'right';
}) {
  return (
    <TableHead className={align === 'right' ? 'text-right' : undefined}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground"
      >
        {label}
        {active === sortKey && <span aria-hidden>↓</span>}
      </button>
    </TableHead>
  );
}

function CategoryBadge({ item }: { item: GlAccountOut }) {
  if (item.category) {
    return (
      <span className="inline-flex items-center gap-1">
        <Badge variant="secondary">
          {CATEGORY_LABELS[item.category as TransactionCategory] ?? item.category}
        </Badge>
        {isDisagreement(item) && (
          <span
            className="cursor-help text-amber-600"
            title={`Keyword classifier suggested: ${
              CATEGORY_LABELS[item.suggested_category as TransactionCategory] ??
              item.suggested_category
            }`}
            aria-label="Classifier disagreement"
          >
            ≠
          </span>
        )}
      </span>
    );
  }
  return <Badge variant="outline">Unclassified</Badge>;
}

function GlAccountDrawer({
  account,
  pending,
  errorText,
  onClose,
  onSave,
}: {
  account: GlAccountOut;
  pending: boolean;
  errorText: string | null;
  onClose: () => void;
  onSave: (fields: {
    category?: TransactionCategory | null;
    gifi_code?: string | null;
    itc_treatment?: string | null;
    is_tax_account?: boolean | null;
    notes?: string | null;
  }) => void;
}) {
  const [category, setCategory] = useState<TransactionCategory | ''>(
    (account.category as TransactionCategory | null) ?? '',
  );
  const [gifiCode, setGifiCode] = useState(account.gifi_code ?? '');
  const [treatment, setTreatment] = useState(account.itc_treatment ?? '');
  const [isTaxAccount, setIsTaxAccount] = useState(account.is_tax_account);
  const [notes, setNotes] = useState(account.notes ?? '');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const suggestion = account.suggested_category as TransactionCategory | null;
  // The drawer is a full-state form: every field is sent, with an
  // explicit null when emptied — the API treats null as "clear" and
  // only absent fields as untouched.
  const save = () =>
    onSave({
      category: category || null,
      gifi_code: gifiCode.trim() || null,
      itc_treatment: treatment || null,
      is_tax_account: isTaxAccount,
      notes: notes.trim() || null,
    });

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close account editor"
        onClick={onClose}
        className="absolute inset-0 bg-brand-navy/50 backdrop-blur-sm"
      />
      <div
        className="relative flex h-full w-full max-w-md flex-col overflow-y-auto border-l border-border bg-card p-6 text-card-foreground shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Classify ${account.gl_account_name}`}
        aria-modal="true"
      >
        <header className="mb-4">
          <h2 className="text-lg font-semibold">{account.gl_account_name}</h2>
          <p className="text-sm text-muted-foreground">
            {account.gl_account_code ?? 'no code'} · {account.account_type ?? 'no type'} ·{' '}
            {account.transaction_count} txns · {formatCurrency(Number(account.total_debit))}
          </p>
        </header>

        <div className="space-y-4">
          <label className="block text-sm">
            <span className="mb-1 block font-medium">Category</span>
            {suggestion && !account.category && (
              <button
                type="button"
                onClick={() => setCategory(suggestion)}
                className="mb-2 inline-flex items-center gap-1 rounded-full border border-brand-royal/40 bg-brand-royal/5 px-2 py-0.5 text-xs hover:bg-brand-royal/10"
              >
                suggested — keyword match: {CATEGORY_LABELS[suggestion] ?? suggestion}
              </button>
            )}
            <FilterSelect
              value={category}
              onChange={(v) => setCategory(v as TransactionCategory | '')}
              options={[
                { value: '', label: 'Unclassified' },
                ...TRANSACTION_CATEGORIES.map((c) => ({ value: c, label: CATEGORY_LABELS[c] })),
              ]}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">GIFI code</span>
            <input
              value={gifiCode}
              onChange={(e) => setGifiCode(e.target.value)}
              maxLength={4}
              placeholder="e.g. 8523"
              className={inputClass}
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">ITC treatment</span>
            <FilterSelect
              value={treatment}
              onChange={setTreatment}
              options={[
                { value: '', label: 'None' },
                ...ITC_TREATMENTS.map((t) => ({ value: t, label: t })),
              ]}
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isTaxAccount}
              onChange={(e) => setIsTaxAccount(e.target.checked)}
            />
            <span>Tax account (GST/HST payable, ITC clearing, …)</span>
          </label>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={512}
              rows={3}
              className={`${inputClass} w-full`}
            />
          </label>
        </div>

        <footer className="mt-6 space-y-3">
          {errorText && (
            <p className="text-sm text-destructive" role="alert">
              {errorText}
            </p>
          )}
          <div className="flex gap-2">
            <Button disabled={pending} onClick={save}>
              {pending ? 'Saving…' : 'Save classification'}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {SOURCE_LABELS[account.classification_source] ?? account.classification_source}
            {account.classified_by ? ` · ${account.classified_by}` : ''}
            {account.classified_at ? ` · ${formatDate(account.classified_at)}` : ''} · first seen{' '}
            {formatDate(account.first_seen_at)} · last seen {formatDate(account.last_seen_at)}
          </p>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
